// supervisor-tick-boundary-directive-drain.ts
//
// The three inbox drains `runSupervisorUntilIdle` performs at every tick
// boundary, in their required order — split out of
// supervisor-run-until-idle.ts to keep that file under the 200-line budget
// (plan 20260924-0205). Behaviour and order are unchanged.
//
// Tick BOUNDARY only — no attempt is in flight when this runs (the previous
// iteration's runOneSupervisorTick already fully resolved), so this is
// exactly the "current worker drains, never killed mid-attempt" point
// spec §7 requires for redirect/answer/revert (test 25). The caller runs it
// BEFORE the dispatch decision, so a directive that lands mid-run takes
// effect on the very next dispatch without restarting `run`.
import { randomUUID } from "node:crypto";
import { tryRevParseHead } from "./commit-owner-git-ops.js";
import { processPendingGateApprovalDirectives } from "./gate-approval-directive-processing.js";
import { rejectInvalidInboxDirectives } from "./inbox-invalid-directive-rejection.js";
import { processPendingInterviewAnswerDirectives } from "./interview-answer-directive-processing.js";
import { processPendingRedirectDirectives } from "./supervisor-directive-processing.js";
import type { RunUntilIdleInput } from "./supervisor-run-until-idle-types.js";

export type TickBoundaryDrainInput = Pick<
  RunUntilIdleInput,
  | "store"
  | "controlRoot"
  | "now"
  | "gitRunner"
  | "repoDir"
  | "notify"
  | "missionId"
  | "evaluatePolicy"
  | "delegatedG3Scope"
>;

/** Journal `directive_rejected` + tell the operator, per rejected directive (R-08, H17). */
async function surfaceRejectedDirectives(
  input: TickBoundaryDrainInput,
  kind: "approve" | "answer",
  rejected: ReadonlyArray<{ directiveId: string; reason: string }>,
): Promise<void> {
  if (rejected.length === 0) return;
  await input.store.commit(
    rejected.map((r) => ({
      event_id: randomUUID(),
      delta: {
        kind: "directive_rejected" as const,
        directive_id: r.directiveId,
        directive_kind: kind,
        reason: r.reason,
      },
    })),
  );
  const label = kind === "approve" ? "Approve" : "Answer";
  for (const r of rejected) input.notify?.(`${label} ${r.directiveId} bị từ chối: ${r.reason}`);
}

export async function drainTickBoundaryDirectives(input: TickBoundaryDrainInput): Promise<void> {
  await rejectInvalidInboxDirectives(input); // F-02(c): sai schema ⇒ journal từ chối, không áp.
  // Bug J (P15): apply pending `approve` directives at the tick boundary.
  // Before this call existed, `avatar autopilot approve g0` wrote a directive
  // nothing ever consumed, so no gate could ever be approved — see
  // gate-approval-directive-processing.ts's header.
  const approvals = await processPendingGateApprovalDirectives({
    store: input.store,
    controlRoot: input.controlRoot,
    now: input.now,
    delegatedG3Scope: input.delegatedG3Scope,
  });
  // R-08: a rejected approve (wrong actor, stale request id, ...) is a
  // decided outcome the processor already marked `.processed` — without
  // this, it vanished silently. Journal it (audit trail survives a
  // gates.json rewrite, same rationale as `gate_approved`) and tell the
  // operator, so a rejection is never mistaken for "nothing happened".
  await surfaceRejectedDirectives(input, "approve", approvals.rejected);

  // B4 (plan 20260915-1650 phase-04): drain `answer` directives into
  // interview.json, so an answer that lands mid-run unblocks the very next
  // dispatch — same reasoning as the gate-approval drain above it.
  //
  // Disjoint from `processPendingRedirectDirectives`: `answer` was removed
  // from REDIRECT_LIKE_KINDS in this phase. Both drainers share ONE
  // `.processed` marker namespace, so leaving `answer` in both would let
  // whichever ran first mark it processed and the other skip it — which is
  // the silent-drop bug B4 exists to fix, reintroduced by a race.
  const answers = await processPendingInterviewAnswerDirectives({
    controlRoot: input.controlRoot,
    store: input.store,
    now: input.now,
  });
  // H17 (audit 2026-09-28): a rejected answer (legacy `--request` payload,
  // unknown question id) got the same silent drop R-08 closed for approve.
  await surfaceRejectedDirectives(input, "answer", answers.rejected);

  const headSha = await tryRevParseHead(input.gitRunner, input.repoDir);
  await processPendingRedirectDirectives({
    store: input.store,
    controlRoot: input.controlRoot,
    repoDir: input.repoDir,
    missionId: input.missionId,
    gitRunner: input.gitRunner,
    evaluatePolicy: input.evaluatePolicy,
    invalidationArtifactHash: headSha ?? "no-head",
    notify: input.notify,
  });
}
